import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, ChevronDown, ChevronUp, User2, Building2 } from "lucide-react";
import api from "@/lib/api";

const FORMATS = ["All", "Standard", "Speed", "Poster"];

export default function AcceptedAbstractsPage() {
  const [content, setContent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [format, setFormat] = useState("All");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/symposium-content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/symposium/accepted-abstracts")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((a) => {
        const fmt = a.assigned_type || a.presentation_type;
        return format === "All" || fmt === format;
      })
      .filter((a) => {
        if (!q) return true;
        const hay = [
          a.abstract_title, a.name, a.surname, a.institution,
          a.keywords, a.co_authors, a.abstract_body,
        ].map((s) => (s || "").toLowerCase()).join(" ");
        return hay.includes(q);
      });
  }, [items, query, format]);

  const grouped = useMemo(() => {
    const g = { Standard: [], Speed: [], Poster: [] };
    for (const a of filtered) {
      const fmt = a.assigned_type || a.presentation_type;
      if (g[fmt]) g[fmt].push(a);
    }
    return g;
  }, [filtered]);

  const totalCounts = useMemo(() => {
    const g = { All: items.length, Standard: 0, Speed: 0, Poster: 0 };
    for (const a of items) {
      const fmt = a.assigned_type || a.presentation_type;
      if (g[fmt] !== undefined) g[fmt] += 1;
    }
    return g;
  }, [items]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div data-testid="accepted-abstracts-page" style={{ backgroundColor: "#fff" }}>
      {/* Hero */}
      <section className="relative h-[320px] md:h-[380px] overflow-hidden" data-testid="programme-hero">
        {content?.hero_image && (
          <img src={content.hero_image} alt={content.name || "Symposium"}
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(120deg, rgba(0,43,65,0.85) 0%, rgba(0,43,65,0.55) 55%, rgba(0,43,65,0.25) 100%)" }}
        />
        <div className="relative ser-container h-full flex flex-col justify-center px-6 md:px-12 text-white">
          <Link
            to="/conferences/symposium-2026"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] mb-3 hover:underline"
            style={{ color: "rgba(255,255,255,0.85)" }}
            data-testid="back-to-symposium"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Symposium
          </Link>
          <p className="eyebrow mb-3" style={{ color: "#fff", opacity: 0.85 }}>Symposium Programme</p>
          <h1 className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] max-w-3xl mb-3" style={{ fontWeight: 300 }}>
            Accepted abstracts
          </h1>
          <p className="text-base md:text-lg max-w-2xl" style={{ color: "rgba(255,255,255,0.85)" }}>
            {content?.dates ? `${content.dates} · ` : ""}{content?.venue_location || "South Africa"}
          </p>
        </div>
      </section>

      {/* Controls */}
      <section className="ser-section" style={{ paddingTop: "3rem" }}>
        <div className="ser-container max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ser-text-muted)" }} />
              <input
                type="text"
                className="ser-input pl-10"
                placeholder="Search by title, presenter, institution, keyword…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="programme-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {FORMATS.map((f) => {
                const active = format === f;
                return (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition"
                    style={{
                      backgroundColor: active ? "var(--ser-primary)" : "var(--ser-section-alt)",
                      color: active ? "white" : "var(--ser-teal)",
                      border: active ? "1px solid var(--ser-primary)" : "1px solid var(--ser-border)",
                    }}
                    data-testid={`programme-filter-${f.toLowerCase()}`}
                  >
                    {f} <span style={{ opacity: 0.7 }}>· {totalCounts[f] ?? 0}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <p className="py-16 text-center" style={{ color: "var(--ser-text-muted)" }}>Loading accepted abstracts…</p>
          ) : items.length === 0 ? (
            <div className="ser-card p-10 text-center" data-testid="programme-empty">
              <p className="eyebrow mb-3" style={{ color: "var(--ser-primary)" }}>Coming soon</p>
              <h2 className="font-serif-display text-2xl mb-3" style={{ color: "var(--ser-primary-dark)" }}>
                The programme is still being assembled
              </h2>
              {content?.abstracts_closed ? (
                <p className="max-w-xl mx-auto text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                  Abstract submissions have now closed. The Scientific Committee is reviewing the
                  submissions received — accepted abstracts will appear on this page as decisions are
                  made. In the meantime, you can{" "}
                  <Link to="/conferences/symposium-2026#register" className="underline" style={{ color: "var(--ser-primary)" }}>
                    register to attend
                  </Link>.
                </p>
              ) : (
                <p className="max-w-xl mx-auto text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                  No abstracts have been accepted yet. The Scientific Committee is reviewing submissions
                  — accepted abstracts will appear on this page as decisions are made.
                  In the meantime, you can{" "}
                  <Link to="/conferences/symposium-2026#abstracts" className="underline" style={{ color: "var(--ser-primary)" }}>
                    submit an abstract
                  </Link>{" "}
                  or{" "}
                  <Link to="/conferences/symposium-2026#register" className="underline" style={{ color: "var(--ser-primary)" }}>
                    register to attend
                  </Link>.
                </p>
              )}
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-16 text-center" style={{ color: "var(--ser-text-muted)" }}>
              No abstracts match your search. Try a different term or clear the filter.
            </p>
          ) : (
            <div className="space-y-14" data-testid="programme-list">
              {["Standard", "Speed", "Poster"].map((fmt) => {
                const group = grouped[fmt];
                if (!group.length) return null;
                return (
                  <div key={fmt} data-testid={`programme-group-${fmt.toLowerCase()}`}>
                    <div className="flex items-baseline gap-4 mb-5 pb-2 border-b" style={{ borderColor: "var(--ser-border)" }}>
                      <h2 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--ser-teal)", fontWeight: 300 }}>
                        {fmt === "Standard" ? "Standard presentations" :
                         fmt === "Speed" ? "Speed talks" : "Poster presentations"}
                      </h2>
                      <span className="text-xs uppercase tracking-wider" style={{ color: "var(--ser-text-muted)" }}>
                        {group.length} accepted
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      {group.map((a) => (
                        <AbstractCard
                          key={a.abstract_id}
                          abstract={a}
                          expanded={!!expanded[a.abstract_id]}
                          onToggle={() => toggle(a.abstract_id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AbstractCard({ abstract: a, expanded, onToggle }) {
  const fmt = a.assigned_type || a.presentation_type;
  const presenter = `${a.title || ""} ${a.name || ""} ${a.surname || ""}`.trim();
  const keywords = (a.keywords || "").split(",").map((k) => k.trim()).filter(Boolean);
  return (
    <article
      className="ser-card p-6 flex flex-col"
      style={{ borderLeft: "3px solid var(--ser-primary)" }}
      data-testid={`abstract-card-${a.abstract_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span
          className="text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5"
          style={{
            backgroundColor: fmt === "Standard" ? "var(--ser-primary)" :
                             fmt === "Speed" ? "var(--ser-teal)" : "var(--ser-clay-dark, #a66435)",
            color: "white",
          }}
        >
          {fmt}
        </span>
        {a.is_student && (
          <span
            className="text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5"
            style={{ backgroundColor: "var(--ser-sand)", color: "var(--ser-primary-dark)" }}
          >
            Student
          </span>
        )}
      </div>
      <h3
        className="font-serif-display text-lg md:text-xl leading-snug mb-3"
        style={{ color: "var(--ser-primary-dark)" }}
      >
        {a.abstract_title || "Untitled abstract"}
      </h3>
      <div className="text-sm mb-2 flex items-start gap-2" style={{ color: "var(--ser-text)" }}>
        <User2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--ser-primary)" }} />
        <span>{presenter || "—"}</span>
      </div>
      {a.institution && (
        <div className="text-sm mb-3 flex items-start gap-2" style={{ color: "var(--ser-text-muted)" }}>
          <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--ser-primary)" }} />
          <span>{a.institution}</span>
        </div>
      )}
      {a.co_authors && (
        <p className="text-xs mb-3" style={{ color: "var(--ser-text-muted)" }}>
          <strong style={{ color: "var(--ser-teal)" }}>Co-authors:</strong> {a.co_authors}
        </p>
      )}
      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {keywords.slice(0, 6).map((k, i) => (
            <span
              key={i}
              className="text-[0.7rem] px-2 py-0.5 rounded-sm"
              style={{ backgroundColor: "var(--ser-section-alt)", color: "var(--ser-teal)" }}
            >
              {k}
            </span>
          ))}
        </div>
      )}
      {a.abstract_body && (
        <div className="mt-auto pt-2 border-t" style={{ borderColor: "var(--ser-border)" }}>
          <button
            type="button"
            onClick={onToggle}
            className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 py-2"
            style={{ color: "var(--ser-primary)" }}
            data-testid={`abstract-toggle-${a.abstract_id}`}
          >
            {expanded ? (<>Hide abstract <ChevronUp className="w-3.5 h-3.5" /></>) :
                        (<>Read abstract <ChevronDown className="w-3.5 h-3.5" /></>)}
          </button>
          {expanded && (
            <p
              className="text-sm leading-relaxed whitespace-pre-wrap pt-2"
              style={{ color: "var(--ser-text)" }}
              data-testid={`abstract-body-${a.abstract_id}`}
            >
              {a.abstract_body}
            </p>
          )}
        </div>
      )}
    </article>
  );
}
